import type { DropdownOption } from '../types';

/** Pick a bilingual (name_en / name_th) label, falling back to the other language. */
export const pickLabel = (nameEn: string | null, nameTh: string | null, language: 'th' | 'en'): string =>
  (language === 'th' ? nameTh || nameEn : nameEn || nameTh) || '';

/**
 * Shared utility functions for prequote forms
 * Includes dropdown formatters and navigation utilities
 */

/**
 * Format generic API response items into dropdown options
 * @param items - Array of any objects from API
 * @returns Array of dropdown options with value (id as string) and label (name)
 */
export const formatOptions = (items: any = []): DropdownOption[] => {
  return items.map((item: any) => ({
    value: item.value,
    label: item.label,
  }));
};

/**
 * Format year options for dropdown
 * @returns Array of year options sorted in descending order
 */
export const dropdownYears = (): DropdownOption[] => {
  const currentYear = new Date().getFullYear();
  const years = [];
  let startYear = 1973;

  while (startYear <= currentYear) {
    years.push(startYear++);
  }

  years.sort((a, b) => b - a);

  return years.map((year) => ({
    value: year.toString(),
    label: year.toString(),
  }));
};

/**
 * URL utilities for prequote forms
 */

/**
 * Builds URL with existing parameters and optional new parameters
 * @param targetPath - Target navigation path
 * @param newParams - Optional new parameters to add/update
 * @returns Complete URL with parameters
 */
export const buildUrlWithParams = (targetPath: string, newParams?: Record<string, string>): string => {
  // Get current URL parameters
  const currentParams = new URLSearchParams(window.location.search);

  // Add new parameters if provided
  if (newParams) {
    Object.entries(newParams).forEach(([key, value]) => {
      if (value) {
        currentParams.set(key, value);
      }
    });
  }

  // Build final URL
  const paramString = currentParams.toString();
  return paramString ? `${targetPath}?${paramString}` : targetPath;
};

/**
 * Get URL parameter by name
 * @param name - Parameter name to search for
 * @returns Parameter value or null
 */
export const getParameterByName = (name: string): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(name);
};
